import { useCallback, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import {
  DELETE_QUESTION_PASSAGE_BANK,
  GET_ONE_QUESTION_PASSAGE_BANK,
  UPDATE_QUESTION_PASSAGE_BANK,
} from 'queries/questionPassageBank'
import { CreateQuestionPassageBankInput } from '__generatedTypes__/globalTypes'
import {
  updateQuestionPassageBank,
  updateQuestionPassageBankVariables,
} from '__generatedTypes__/updateQuestionPassageBank'
import {
  findOneQuestionPassageBank,
  findOneQuestionPassageBankVariables,
} from '__generatedTypes__/findOneQuestionPassageBank'
import {
  removeQuestionPassageBank,
  removeQuestionPassageBankVariables,
} from '__generatedTypes__/removeQuestionPassageBank'
import { useHistory } from 'hooks/useHistory'
import { PageLoader } from 'components/PageLoader'
import { toast } from 'utils/toast'
import { getLevelOption } from 'pagesNew/QuestionPassages/Form/consts'
import { Form, QuestionPassageBank } from '../Form'

export const Edit = () => {
  const { carsQuestionBankId = '' } = useParams()
  const { navigate } = useLink()
  const { loadingHistory, populateHistoryWithUser } = useHistory()
  const [update] = useMutation<updateQuestionPassageBank, updateQuestionPassageBankVariables>(
    UPDATE_QUESTION_PASSAGE_BANK
  )
  const [remove] = useMutation<removeQuestionPassageBank, removeQuestionPassageBankVariables>(
    DELETE_QUESTION_PASSAGE_BANK
  )
  const { data, loading } = useQuery<findOneQuestionPassageBank, findOneQuestionPassageBankVariables>(
    GET_ONE_QUESTION_PASSAGE_BANK,
    {
      variables: {
        params: carsQuestionBankId,
      },
      fetchPolicy: 'network-only',
    }
  )
  const questionPassageBank = data?.findOneQuestionPassageBank

  const handleSubmit = useCallback(
    async (questionPassageBank: CreateQuestionPassageBankInput) => {
      try {
        const { data } = await update({
          variables: {
            params: {
              id: carsQuestionBankId,
              ...questionPassageBank,
              history: populateHistoryWithUser(questionPassageBank.history),
            },
          },
        })

        if (data?.updateQuestionPassageBank.id) {
          navigate(cmsRoutes.CARS_QUESTION_BANK_LIST, undefined, {
            searchParams: new URLSearchParams({
              subjectId: questionPassageBank.subject?.id ?? '',
            }),
          })
          toast.success('CARS Question Bank updated successfully')
        } else {
          toast.error('Failed to update CARS Question Bank')
        }
      } catch (error) {
        toast.error('Failed to update CARS Question Bank')
      }
    },
    [populateHistoryWithUser, carsQuestionBankId, questionPassageBank, update, navigate]
  )

  const handleDelete = useCallback(async () => {
    try {
      const { data } = await remove({
        variables: {
          params: carsQuestionBankId,
        },
      })

      if (data?.removeQuestionPassageBank) {
        navigate(cmsRoutes.CARS_QUESTION_BANK_LIST, undefined, {
          searchParams: new URLSearchParams({
            subjectId: questionPassageBank?.subject.id ?? '',
          }),
        })
        toast.success('CARS Question Bank removed successfully')
      } else {
        toast.error('Failed to remove CARS Question Bank')
      }
    } catch (error) {
      toast.error('Failed to remove CARS Question Bank')
    }
  }, [carsQuestionBankId, questionPassageBank, remove, navigate])

  useEffect(() => {
    if (!loading && !questionPassageBank) {
      navigate(cmsRoutes.CARS_QUESTION_BANK_LIST)
      toast.error('Failed to load data, redirecting to CARS Question Bank List')
    }
  }, [loading, questionPassageBank])

  if (loading || !questionPassageBank || loadingHistory) {
    return <PageLoader />
  }

  const level = getLevelOption(questionPassageBank.level)

  return (
    <Form
      questionPassageBank={
        {
          ...questionPassageBank,
          level,
        } as QuestionPassageBank
      }
      subject={questionPassageBank.subject}
      level={level}
      availableLevels={[level]}
      onSubmit={handleSubmit}
      onDelete={handleDelete}
    />
  )
}
